/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisStepper } from 'src/core/components/luisStepper/LuisStepper';
import { LuisStepperStepType } from 'src/core/components/luisStepper/LuisStepperStep';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';

export type AutotagTriggerStepsNavigatorProps = {
    activeStep: number;
    horizontal?: boolean;
    onActiveStepChange: (newStep: number) => void;
};

const messages = defineMessages({
    overview: {
        id: 'customText.azureMLConnectionStepsNavigator.overview',
        defaultMessage: 'Overview'
    },
    connection: {
        id: 'customText.azureMLConnectionStepsNavigator.connection',
        defaultMessage: 'Project creation'
    },
    outsourcing: {
        id: 'customText.azureMLConnectionStepsNavigator.outsourcing',
        defaultMessage: 'Outsourcing (optional)'
    },
    instructions: {
        id: 'customText.azureMLConnectionStepsNavigator.instructions',
        defaultMessage: 'Instructions (optional)'
    },
    review: {
        id: 'customText.azureMLConnectionStepsNavigator.review',
        defaultMessage: 'Review'
    }
});

/**
 * Displays the side navigator in the autotag trigger modal.
 */
export const AzureMLConnectionStepsNavigator = (props: AutotagTriggerStepsNavigatorProps) => {
    const { activeStep, onActiveStepChange, horizontal } = props;

    const { localize } = useLocalization();

    const getStepStatus = (stepIndex: AMLConnectionSteps): 'none' | 'completed' | 'active' => {
        if (stepIndex > activeStep) {
            return 'none';
        }
        if (stepIndex === activeStep) {
            return 'active';
        }

        return 'completed';
    };

    const steps: LuisStepperStepType[] = [
        {
            id: 'overview',
            title: localize(messages.overview),
            disabled: activeStep < AMLConnectionSteps.Overview,
            state: getStepStatus(AMLConnectionSteps.Overview)
        },
        {
            id: 'connection',
            title: localize(messages.connection),
            disabled: activeStep < AMLConnectionSteps.Connection,
            state: getStepStatus(AMLConnectionSteps.Connection)
        },
        {
            id: 'outsourcing',
            title: localize(messages.outsourcing),
            disabled: activeStep < AMLConnectionSteps.Outsourcing,
            state: getStepStatus(AMLConnectionSteps.Outsourcing)
        },
        {
            id: 'instructions',
            title: localize(messages.instructions),
            disabled: activeStep < AMLConnectionSteps.Instructions,
            state: getStepStatus(AMLConnectionSteps.Instructions)
        },
        {
            id: 'review',
            title: localize(messages.review),
            disabled: activeStep < AMLConnectionSteps.Review,
            state: getStepStatus(AMLConnectionSteps.Review)
        }
    ];

    const onStepClick = (stepKey: string) => onActiveStepChange(steps.findIndex(step => step.id === stepKey));

    return <LuisStepper horizontal={horizontal} onStepClick={onStepClick} steps={steps} />;
};
